"use client";

import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { defaultLocale, type Locale } from "@/lib/i18n";
import { useIsMobile } from "@/hooks/use-mobile";

interface HeroSectionMinimalProps {
  locale?: Locale;
}

const heroTitle: Record<Locale, string> = {
  de: "seit 1968",
  en: "since 1968",
  fr: "depuis 1968",
  it: "dal 1968",
};

const heroDescriptive: Record<Locale, string> = {
  de: "W. Althaus AG – Industrielle Automation aus der Schweiz",
  en: "W. Althaus AG – Industrial Automation from Switzerland",
  fr: "W. Althaus AG – Automatisation industrielle suisse",
  it: "W. Althaus AG – Automazione industriale svizzera",
};

const statements: Record<Locale, string> = {
  de: "Seit über 50 Jahren steht die W.\u00A0Althaus\u00A0AG in Aarwangen für industrielle Automation in der Schweiz.",
  en: "For over 50 years, W.\u00A0Althaus\u00A0AG in Aarwangen has stood for industrial automation in Switzerland.",
  fr: "Depuis plus de 50 ans, W.\u00A0Althaus\u00A0AG à Aarwangen est synonyme d'automatisation industrielle en Suisse.",
  it: "Da oltre 50 anni, W.\u00A0Althaus\u00A0AG ad Aarwangen è sinonimo di automazione industriale in Svizzera.",
};


export function HeroSectionMinimal({ locale = defaultLocale }: HeroSectionMinimalProps) {
  const isMobile = useIsMobile();
  // Render only after we know viewport to avoid loading both videos.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  return (
    <>
      {/* Video Hero Section - Clean */}
      {/* Mobile Video */}
      <section className="relative w-full min-h-screen h-screen h-[100svh] bg-background overflow-hidden block md:hidden">
        {mounted && isMobile && (
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src="/Headerbild_Mobile_Webseite.mp4" type="video/mp4" />
          </video>
        )}
        <div className="absolute inset-0 bg-black/15 z-10" />
        <div className="absolute inset-0 z-20 flex items-center justify-center px-8">
          <div className="text-center space-y-2">
            <h1 className="text-3xl font-semibold tracking-wide text-white">
              <span className="sr-only">{heroDescriptive[locale]} – </span>
              <span aria-hidden="true">{heroTitle[locale]}</span>
            </h1>
            <p className="text-3xl font-semibold tracking-wide" style={{ color: 'rgb(0, 131, 138)' }}>
              Leading in Automation
            </p>
          </div>
        </div>
      </section>

      {/* Desktop Video */}
      <section className="relative w-full min-h-screen h-screen bg-background overflow-hidden hidden md:block">
        {mounted && !isMobile && (
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src="/Imagefilm_short720pV2.mp4" type="video/mp4" />
          </video>
        )}
        <div className="absolute inset-0 bg-black/15 z-10" />
        <div className="absolute inset-0 z-20 flex items-end justify-center px-8" style={{ paddingBottom: 'calc(20vh + 118px)' }}>
          <div className="text-center space-y-2">
            <h1 className="text-5xl lg:text-6xl font-semibold tracking-wide text-white">
              <span className="sr-only">{heroDescriptive[locale]} – </span>
              <span aria-hidden="true">{heroTitle[locale]}</span>
            </h1>
            <p className="text-5xl lg:text-6xl font-semibold tracking-wide" style={{ color: 'rgb(0, 131, 138)' }}>
              Leading in Automation
            </p>
          </div>
        </div>
      </section>

      {/* Statement Section - Pure Minimalism */}
      <StatementSection text={statements[locale] || statements.de} />
    </>
  );
}

function StatementSection({ text }: { text: string }) {
  const prefersReducedMotion = useReducedMotion();
  const [displayed, setDisplayed] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  // Start sobald die Sektion sichtbar wird (nur einmal)
  useEffect(() => {
    if (!ref.current || started) return;
    const el = ref.current;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.4) {
            setStarted(true);
            observer.disconnect();
            break;
          }
        }
      },
      { threshold: [0, 0.4, 1] }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [started]);

  // Typewriter via requestAnimationFrame – flüssig, frame-synchron
  useEffect(() => {
    if (!started) return;
    if (prefersReducedMotion) {
      setDisplayed(text.length);
      return;
    }

    let raf = 0;
    let startTs = 0;
    // Geplante Endzeit jedes Zeichens (kumulativ in ms)
    const charsPerSecond = 24; // gleichmäßiges Grundtempo
    const baseStep = 1000 / charsPerSecond;
    const schedule: number[] = [];
    let acc = 0;
    for (let i = 0; i < text.length; i++) {
      const ch = text.charAt(i);
      // sanfte, kleinere Pausen als zuvor – wirkt natürlicher, nicht stockend
      const extra =
        ch === "." ? 260 :
        ch === "," ? 120 :
        ch === " " ? -8 :
        0;
      acc += baseStep + extra;
      schedule.push(acc);
    }
    const total = schedule[schedule.length - 1] ?? 0;

    const tick = (ts: number) => {
      if (!startTs) startTs = ts;
      const elapsed = ts - startTs;
      // Binäre Suche: wie viele Zeichen sind „fällig"
      let lo = 0;
      let hi = schedule.length;
      while (lo < hi) {
        const mid = (lo + hi) >>> 1;
        if (schedule[mid] <= elapsed) lo = mid + 1;
        else hi = mid;
      }
      setDisplayed(lo);
      if (elapsed < total) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [started, text, prefersReducedMotion]);

  const done = displayed >= text.length;

  return (
    <section
      ref={ref}
      className="py-24 md:py-36 lg:py-48 flex items-center justify-center px-8 md:px-12 bg-white dark:bg-background"
    >
      <p
        className="relative max-w-5xl w-full text-center text-3xl md:text-[45px] font-normal text-foreground leading-relaxed tracking-wide"
      >
        {/* Höhen-Platzhalter, damit das Layout nicht springt — bleibt im a11y-Tree für Screenreader */}
        <span className="invisible">
          {text}
        </span>
        <span className="absolute inset-0" aria-hidden="true">
          {text.slice(0, displayed)}
          {!done && (
            <span className="inline-block w-[0.06em] h-[0.9em] align-[-0.1em] ml-[0.05em] bg-current animate-pulse" />
          )}
        </span>
      </p>
    </section>
  );
}
